// frontend/src/components/common/BulkImportDialog.jsx
//
// The one import dialog. Every merchant file upload in the SaaS goes through
// it: prepare, choose a file, declare how it should be applied, upload, read
// the result.
//
// Entities differ only in the CONTROLS one needs and another doesn't, so the
// controls are a slot (`options`) and the envelope belongs to ImportResult.
//
// What the dialog is allowed to claim about an import — whether it creates
// records, which columns a checkbox will empty — comes from the server via
// useTabularEntity, never from a page prop that can drift from it.

import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  LinearProgress,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { ICON, FileXls, X } from '../../icons';

import { DownloadIcon, UploadIcon } from '../../icons';
import { useTabularEntity } from '../../hooks/useTabularEntity';
import DialogHeading from './DialogHeading';
import ImportResult from './ImportResult';
import '../../styles/dialog-form.css';

/**
 * @param {Object}    props
 * @param {boolean}   props.open
 * @param {Function}  props.onClose
 * @param {Object}    props.importHook - return value of useBulkImport()
 * @param {string}    props.entityKey - the register's key ('items', ...).
 *   Drives every claim the dialog makes (see useTabularEntity) and names
 *   the entity in the title (export.entity.<key>).
 * @param {string}    [props.title] - full title, when the entity needs its own
 *   phrasing
 * @param {string}    [props.subtitle] - the record the import fills (the
 *   supplier catalog's supplier), named under the title
 * @param {ReactNode} [props.options] - entity-specific "how to apply" controls
 * @param {Array}     [props.counters] - per-kind result lines; see ImportResult
 * @param {string}    [props.prepareHint] - extra preparation guidance only
 *   this entity can give, shown under the template button
 */
export default function BulkImportDialog({
  open, onClose, importHook, entityKey,
  title, subtitle = null, options, counters, prepareHint,
}) {
  const { t } = useTranslation();
  const {
    updateOnly, clearableLabels,
    isLoading: entityLoading, error: entityError,
  } = useTabularEntity(entityKey);
  const {
    file, loading, result, error,
    fileInputRef, selectFile, upload, reset,
    downloadTemplate, downloadErrors,
    clearBlanks, setClearBlanks,
  } = importHook;

  const hiddenInput = useRef(null);
  const isDone = !!result?.summary;
  // While the entity is unknown the dialog must not describe the import.
  const canDescribe = !entityError && !entityLoading;

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleClose}
      className="dialog-form"
    >
      <DialogTitle>
        <DialogHeading
          title={title || t(updateOnly ? 'bulkImport.titleUpdate' : 'bulkImport.title',
                            { entity: t(`export.entity.${entityKey}`) })}
          subtitle={subtitle}
        />
        <IconButton onClick={handleClose} size="small" disabled={loading} aria-label={t('btn.close')}>
          <X size={ICON.xl} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* If the server could not tell us what this import does, the dialog
            says nothing about it. Every plausible stand-in — "assume it can
            create", "assume nothing is clearable" — is a promise made on the
            server's behalf that it never agreed to. */}
        {entityError && (
          <Alert severity="error" variant="outlined">
            {t('bulkImport.entityUnavailable')}
          </Alert>
        )}

        {/* Prepare */}
        <Typography className="dialog-form-section">
          {t('bulkImport.prepareSection')}
        </Typography>
        {/* The template exists only where the import can create records, so
            the button appears only there too. On an update-only import a
            blank sheet would invite a file of brand-new identifiers whose
            every row comes back unmatched — the round trip that started this
            work. Those entities are told to edit their export instead. */}
        {canDescribe && !updateOnly && (
          <>
            <Typography variant="body2" color="text.secondary">
              {t('bulkImport.prepareNewDescription')}
            </Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon size={ICON.md} />}
                onClick={downloadTemplate}
                size="small"
              >
                {t('bulkImport.downloadTemplate')}
              </Button>
            </Box>
          </>
        )}
        {/* Entity-specific preparation guidance, where the generic copy isn't
            the whole story (the supplier catalog can seed a file from another
            supplier's export — advice no other import can give). */}
        {prepareHint && canDescribe && (
          <Typography variant="body2" color="text.secondary">
            {prepareHint}
          </Typography>
        )}
        {canDescribe && (
          <Typography variant="body2" color="text.secondary">
            {t(updateOnly
              ? 'bulkImport.prepareEditDescriptionUpdateOnly'
              : 'bulkImport.prepareEditDescription')}
          </Typography>
        )}

        {/* Upload */}
        <Typography className="dialog-form-section">
          {t('bulkImport.uploadSection')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('bulkImport.uploadDescription')}
        </Typography>

        <input
          ref={(el) => { hiddenInput.current = el; if (fileInputRef) fileInputRef.current = el; }}
          type="file"
          accept=".xlsx,.csv"
          onChange={(e) => selectFile(e.target.files?.[0])}
          style={{ display: 'none' }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<FileXls size={ICON.md} />}
            onClick={() => hiddenInput.current?.click()}
            size="small"
            disabled={loading}
          >
            {t('bulkImport.chooseFile')}
          </Button>
          {file && (
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
              {file.name}
            </Typography>
          )}
        </Box>

        {/* How to apply — the entity's own controls, plus the blank-cell
            policy wherever the server says a column is governed by it. */}
        {!isDone && (options || clearableLabels.length > 0) && (
          <>
            <Typography className="dialog-form-section">
              {t('bulkImport.applySection')}
            </Typography>
            {options}
            {clearableLabels.length > 0 && (
              /* Declared with the file because the file cannot express it: an
                 empty cell looks identical whether the merchant deleted the
                 value or never filled it in. The hint names the affected
                 columns — an unlabelled destructive toggle is not consent. */
              <FormControlLabel
                sx={{ alignItems: 'flex-start' }}
                control={
                  <Checkbox
                    size="small"
                    checked={!!clearBlanks}
                    onChange={(e) => setClearBlanks(e.target.checked)}
                    disabled={loading}
                  />
                }
                label={
                  <Box sx={{ pt: 0.5 }}>
                    <Typography variant="body2">
                      {t('bulkImport.clearBlanksLabel')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('bulkImport.clearBlanksHint', {
                        fields: clearableLabels.join(', '),
                      })}
                    </Typography>
                  </Box>
                }
              />
            )}
          </>
        )}

        {/* Client-side refusal (wrong extension, too large) or a transport
            failure. Row-level problems are data and live in ImportResult. */}
        {error && !isDone && (
          <Alert severity="error" variant="outlined">{error}</Alert>
        )}

        {loading && (
          <Box>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {t('bulkImport.uploading')}
            </Typography>
          </Box>
        )}

        <ImportResult
          result={result}
          counters={counters}
          onDownloadErrors={downloadErrors}
        />
      </DialogContent>

      <DialogActions>
        {isDone ? (
          <Button variant="contained" onClick={handleClose}>
            {t('btn.close')}
          </Button>
        ) : (
          <>
            <Button variant="outlined" onClick={handleClose} disabled={loading}>
              {t('btn.cancel')}
            </Button>
            <Button
              variant="contained"
              startIcon={<UploadIcon size={ICON.md} />}
              onClick={upload}
              disabled={!file || entityLoading || !!entityError}
              loading={loading}
            >
              {t('bulkImport.upload')}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
